import { useState, useEffect, useCallback } from "react";
import ActionBar from "../../components/ActionBar/ActionBar";
import Spinner from "../../components/Spinner/Spinner";
import CircleChart from "../../components/CircleChart/CircleChart";
import { wipeUser, cleanUserData } from "../../api/users";
import { getStatistics } from "../../api/superAdmin";
import {
  getInvoiceOrganizations,
  generateInvoice,
  generateAllMonthlyInvoices,
} from "../../api/organizations/organizations";
import { colorOptions } from "../../utils/colors";
import useAuthStore from "../../store/authStore";
import { useSocketEvent } from "../../hooks/useRealtime";
import SystemBannersTab from "./SystemBannersTab/SystemBannersTab";
import "./SuperDashboard.scss";

// Named colors for charts
const COLORS = {
  purple: colorOptions[15], // orchid
  green: colorOptions[7], // forest
  orange: colorOptions[2], // coral
  yellow: colorOptions[4], // lemon
  blue: colorOptions[12], // denim
  pink: colorOptions[16], // magenta
  teal: colorOptions[9], // turquoise
  rose: colorOptions[1], // raspberry
  gray: colorOptions[21], // slate
  indigo: colorOptions[13], // slate
};

function SuperDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [wipeEmail, setWipeEmail] = useState("");
  const [isWiping, setIsWiping] = useState(false);
  const [wipeResult, setWipeResult] = useState(null);

  const [cleanEmail, setCleanEmail] = useState("");
  const [isCleaning, setIsCleaning] = useState(false);
  const [cleanResult, setCleanResult] = useState(null);

  const [statistics, setStatistics] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchText, setUserSearchText] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserStats, setSelectedUserStats] = useState(null);
  const [loadingUserStats, setLoadingUserStats] = useState(false);
  const [selectedDetailType, setSelectedDetailType] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Enterprise/Invoice billing state
  const [enterpriseOrgs, setEnterpriseOrgs] = useState([]);
  const [loadingEnterprise, setLoadingEnterprise] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(null);
  const [generatingAll, setGeneratingAll] = useState(false);

  const token = useAuthStore((state) => state.token);

  // Fetch statistics on mount
  const fetchStatistics = useCallback(async () => {
    try {
      setLoadingStats(true);
      const data = await getStatistics(token);
      setStatistics(data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    } finally {
      setLoadingStats(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === "overview") {
      fetchStatistics();
    }
  }, [activeTab, fetchStatistics]);

  // Fetch users when Users tab is active
  const fetchUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || "http://localhost:5001"}/api/users/all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
          cache: "no-store",
        },
      );
      const data = await response.json();
      if (response.ok) {
        setUsers(data);
      } else {
        console.error("Error fetching users:", data.message);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoadingUsers(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === "users") {
      fetchUsers();
    }
  }, [activeTab, fetchUsers]);

  // Fetch enterprise organizations when Enterprise tab is active
  const fetchEnterpriseOrgs = useCallback(async () => {
    try {
      setLoadingEnterprise(true);
      const data = await getInvoiceOrganizations(token);
      setEnterpriseOrgs(data);
    } catch (error) {
      console.error("Error fetching enterprise organizations:", error);
    } finally {
      setLoadingEnterprise(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === "enterprise") {
      fetchEnterpriseOrgs();
    }
  }, [activeTab, fetchEnterpriseOrgs]);

  // Fetch selected user statistics
  const fetchUserStats = useCallback(async () => {
    if (!selectedUser) {
      setSelectedUserStats(null);
      return;
    }

    try {
      setLoadingUserStats(true);
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || "http://localhost:5001"}/api/users/${selectedUser._id}/stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
          cache: "no-store",
        },
      );
      const data = await response.json();
      if (response.ok) {
        setSelectedUserStats(data);

        // Automatically fetch tasks and notes counts to update statistics
        try {
          const [tasksResponse, notesResponse] = await Promise.all([
            fetch(
              `${import.meta.env.VITE_API_URL || "http://localhost:5001"}/api/users/${selectedUser._id}/details/tasks`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Cache-Control": "no-cache",
                  Pragma: "no-cache",
                },
                cache: "no-store",
              },
            ),
            fetch(
              `${import.meta.env.VITE_API_URL || "http://localhost:5001"}/api/users/${selectedUser._id}/details/notes`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Cache-Control": "no-cache",
                  Pragma: "no-cache",
                },
                cache: "no-store",
              },
            ),
          ]);

          const tasksData = await tasksResponse.json();
          const notesData = await notesResponse.json();

          if (tasksResponse.ok && notesResponse.ok) {
            const updatedStats = { ...data };
            updatedStats.statistics.tasks = tasksData.data?.length || 0;
            updatedStats.statistics.notes = notesData.data?.length || 0;
            setSelectedUserStats(updatedStats);
          }
        } catch (error) {
          console.error("Error fetching task/note counts:", error);
          // Keep the original stats if count fetch fails
        }
      } else {
        console.error("Error fetching user stats:", data.message);
      }
    } catch (error) {
      console.error("Error fetching user stats:", error);
    } finally {
      setLoadingUserStats(false);
    }
  }, [selectedUser, token]);

  useEffect(() => {
    fetchUserStats();
  }, [fetchUserStats]);

  // Fetch detailed data for a specific type
  const fetchDetailData = useCallback(
    async (type) => {
      if (!selectedUser) return;

      try {
        setLoadingDetails(true);
        setSelectedDetailType(type);
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || "http://localhost:5001"}/api/users/${selectedUser._id}/details/${type}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
            cache: "no-store",
          },
        );
        const data = await response.json();
        if (response.ok) {
          setDetailData(data.data);

          // Update the statistics with actual counts from detail data (only if changed)
          if (selectedUserStats && data.data) {
            if (
              type === "tasks" &&
              selectedUserStats.statistics.tasks !== data.data.length
            ) {
              setSelectedUserStats((prev) => ({
                ...prev,
                statistics: { ...prev.statistics, tasks: data.data.length },
              }));
            } else if (
              type === "notes" &&
              selectedUserStats.statistics.notes !== data.data.length
            ) {
              setSelectedUserStats((prev) => ({
                ...prev,
                statistics: { ...prev.statistics, notes: data.data.length },
              }));
            }
          }
        } else {
          console.error("Error fetching detail data:", data.message);
        }
      } catch (error) {
        console.error("Error fetching detail data:", error);
      } finally {
        setLoadingDetails(false);
      }
    },
    [selectedUser, token, selectedUserStats],
  );

  // Reset detail view when user changes
  useEffect(() => {
    setSelectedDetailType(null);
    setDetailData(null);
  }, [selectedUser]);

  // Initial data fetch on component mount
  useEffect(() => {
    // Fetch statistics immediately when component mounts
    fetchStatistics();
    // If we're on the users tab, fetch users too
    if (activeTab === "users") {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - we want to force refresh on load

  // WebSocket listeners for real-time updates
  // Listen for organization events
  useSocketEvent("organization:created", fetchStatistics);
  useSocketEvent("organization:updated", fetchStatistics);
  useSocketEvent("organization:deleted", fetchStatistics);
  useSocketEvent("organization:member-added", () => {
    fetchStatistics();
    fetchUserStats();
  });
  useSocketEvent("organization:member-removed", () => {
    fetchStatistics();
    fetchUserStats();
  });

  // Listen for project events
  useSocketEvent("project:created", () => {
    fetchStatistics();
    fetchUserStats();
  });
  useSocketEvent("project:updated", () => {
    fetchStatistics();
    fetchUserStats();
  });
  useSocketEvent("project:deleted", () => {
    fetchStatistics();
    fetchUserStats();
  });

  // Listen for team events
  useSocketEvent("team:created", () => {
    fetchStatistics();
    fetchUserStats();
  });
  useSocketEvent("team:updated", () => {
    fetchStatistics();
    fetchUserStats();
  });
  useSocketEvent("team:deleted", () => {
    fetchStatistics();
    fetchUserStats();
  });

  // Listen for task events
  useSocketEvent("task:created", () => {
    fetchStatistics();
    fetchUserStats();
  });
  useSocketEvent("task:updated", () => {
    fetchStatistics();
    fetchUserStats();
  });
  useSocketEvent("task:deleted", () => {
    fetchStatistics();
    fetchUserStats();
  });
  useSocketEvent("task:bulk-deleted", () => {
    fetchStatistics();
    fetchUserStats();
  });

  // Listen for note events
  useSocketEvent("note:created", () => {
    fetchStatistics();
    fetchUserStats();
  });
  useSocketEvent("note:updated", () => {
    fetchStatistics();
    fetchUserStats();
  });
  useSocketEvent("note:deleted", () => {
    fetchStatistics();
    fetchUserStats();
  });
  useSocketEvent("note:bulk-deleted", () => {
    fetchStatistics();
    fetchUserStats();
  });

  // Listen for user events
  useSocketEvent("user:created", () => {
    fetchStatistics();
    fetchUsers();
  });
  useSocketEvent("user:updated", () => {
    fetchStatistics();
    fetchUsers();
    fetchUserStats();
  });
  useSocketEvent("user:deleted", () => {
    fetchStatistics();
    fetchUsers();
  });

  const handleCleanUserData = async () => {
    if (!cleanEmail.trim()) {
      setCleanResult({
        success: false,
        message: "Please enter an email address",
      });
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to clean all data for user: ${cleanEmail}?\n\nThis will:\n- Remove from all organizations and projects\n- Delete owned organizations\n- Clear all tasks and notes\n- Keep the user account active\n\nThis action CANNOT be undone!`,
    );

    if (!confirmed) return;

    setIsCleaning(true);
    setCleanResult(null);

    try {
      const result = await cleanUserData(cleanEmail, token);
      setCleanResult({
        success: true,
        message: result.message,
        details: result.details,
      });
      setCleanEmail("");
    } catch (error) {
      setCleanResult({
        success: false,
        message: error.message || "Failed to clean user data",
      });
    } finally {
      setIsCleaning(false);
    }
  };

  const handleWipeUser = async () => {
    if (!wipeEmail.trim()) {
      setWipeResult({
        success: false,
        message: "Please enter an email address",
      });
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to completely wipe the user with email: ${wipeEmail}?\n\nThis will:\n- Delete the user account\n- Remove from all organizations and projects\n- Delete owned organizations\n- This action CANNOT be undone!`,
    );

    if (!confirmed) return;

    setIsWiping(true);
    setWipeResult(null);

    try {
      const result = await wipeUser(wipeEmail, token);
      setWipeResult({
        success: true,
        message: result.message,
        details: result.details,
      });
      setWipeEmail("");
    } catch (error) {
      setWipeResult({
        success: false,
        message: error.message || "Failed to wipe user",
      });
    } finally {
      setIsWiping(false);
    }
  };

  const handleDeleteUser = (user, e) => {
    e.stopPropagation(); // Prevent selecting the user
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);

    try {
      const result = await wipeUser(userToDelete.email, token);

      // Remove from users list
      setUsers(users.filter((u) => u._id !== userToDelete._id));

      // Clear selected user if it was the deleted one
      if (selectedUser?._id === userToDelete._id) {
        setSelectedUser(null);
      }

      // Show success message (you can add a toast/notification here)
      console.log("User deleted successfully:", result);

      // Close modal
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (error) {
      console.error("Error deleting user:", error);
      alert(error.message || "Failed to delete user");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  return (
    <div className="super-dashboard-wrapper">
      {/* <ActionBar page="super-dashboard" /> */}
      <div className="super-dashboard">
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            className={`tab-button ${activeTab === "users" ? "active" : ""}`}
            onClick={() => setActiveTab("users")}
          >
            Users
          </button>
          <button
            className={`tab-button ${activeTab === "enterprise" ? "active" : ""}`}
            onClick={() => setActiveTab("enterprise")}
          >
            Enterprise
          </button>
          <button
            className={`tab-button ${activeTab === "system-notification" ? "active" : ""}`}
            onClick={() => setActiveTab("system-notification")}
          >
            System Notification
          </button>
          <button
            className={`tab-button ${activeTab === "stash" ? "active" : ""}`}
            onClick={() => setActiveTab("stash")}
          >
            Stash
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === "overview" && (
            <div className="super-dashboard-overview">
              {loadingStats ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "400px",
                  }}
                >
                  <Spinner />
                </div>
              ) : (
                <div className="overview-tiles">
                  {/* Total Users */}
                  <div className="overview-tile">
                    <div className="tile-title">
                      <div className="tile-icon">
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: "27px", color: COLORS.purple }}
                        >
                          group
                        </span>
                      </div>
                      <div className="tile-title-text">Total Users</div>
                    </div>
                    <div className="content">
                      <CircleChart
                        segments={[
                          {
                            label: "Active",
                            value: statistics?.users?.active || 0,
                            percentage:
                              ((statistics?.users?.active || 0) /
                                (statistics?.users?.total || 1)) *
                              100,
                            color: COLORS.green,
                          },
                          {
                            label: "Inactive",
                            value: statistics?.users?.inactive || 0,
                            percentage:
                              ((statistics?.users?.inactive || 0) /
                                (statistics?.users?.total || 1)) *
                              100,
                            color: COLORS.gray,
                          },
                        ]}
                        size={120}
                        strokeWidth={6}
                        centerValue={statistics?.users?.total || 0}
                        centerLabel="Total"
                        showLegend={true}
                        legendPosition="right"
                        showValues={true}
                        animate={true}
                      />
                    </div>
                  </div>

                  {/* Total Organizations */}
                  <div className="overview-tile">
                    <div className="tile-title">
                      <div className="tile-icon">
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: "27px", color: COLORS.indigo }}
                        >
                          business
                        </span>
                      </div>
                      <div className="tile-title-text">Organizations</div>
                    </div>
                    <div className="content">
                      <CircleChart
                        segments={[
                          {
                            label: "Total",
                            value: statistics?.organizations?.total || 0,
                            percentage: 100,
                            color: COLORS.indigo,
                          },
                        ]}
                        size={120}
                        strokeWidth={6}
                        centerValue={statistics?.organizations?.total || 0}
                        centerLabel="Total"
                        showLegend={true}
                        legendPosition="right"
                        showValues={true}
                        animate={true}
                      />
                    </div>
                  </div>

                  {/* Total Projects */}
                  <div className="overview-tile">
                    <div className="tile-title">
                      <div className="tile-icon">
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: "27px", color: COLORS.blue }}
                        >
                          folder_copy
                        </span>
                      </div>
                      <div className="tile-title-text">Projects</div>
                    </div>
                    <div className="content">
                      <CircleChart
                        segments={[
                          {
                            label: "Total",
                            value: statistics?.projects?.total || 0,
                            percentage: 100,
                            color: COLORS.blue,
                          },
                        ]}
                        size={120}
                        strokeWidth={6}
                        centerValue={statistics?.projects?.total || 0}
                        centerLabel="Total"
                        showLegend={true}
                        legendPosition="right"
                        showValues={true}
                        animate={true}
                      />
                    </div>
                  </div>

                  {/* Total Tasks */}
                  <div className="overview-tile">
                    <div className="tile-title">
                      <div className="tile-icon">
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: "27px", color: COLORS.orange }}
                        >
                          task_alt
                        </span>
                      </div>
                      <div className="tile-title-text">Tasks</div>
                    </div>
                    <div className="content">
                      <CircleChart
                        segments={[
                          {
                            label: "Completed",
                            value: statistics?.tasks?.completed || 0,
                            percentage:
                              ((statistics?.tasks?.completed || 0) /
                                (statistics?.tasks?.total || 1)) *
                              100,
                            color: COLORS.green,
                          },
                          {
                            label: "Active",
                            value: statistics?.tasks?.active || 0,
                            percentage:
                              ((statistics?.tasks?.active || 0) /
                                (statistics?.tasks?.total || 1)) *
                              100,
                            color: COLORS.orange,
                          },
                        ]}
                        size={120}
                        strokeWidth={6}
                        centerValue={statistics?.tasks?.total || 0}
                        centerLabel="Total"
                        showLegend={true}
                        legendPosition="right"
                        showValues={true}
                        animate={true}
                      />
                    </div>
                  </div>

                  {/* Total Notes */}
                  <div className="overview-tile">
                    <div className="tile-title">
                      <div className="tile-icon">
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: "27px", color: COLORS.yellow }}
                        >
                          note
                        </span>
                      </div>
                      <div className="tile-title-text">Notes</div>
                    </div>
                    <div className="content">
                      <CircleChart
                        segments={[
                          {
                            label: "Total",
                            value: statistics?.notes?.total || 0,
                            percentage: 100,
                            color: COLORS.yellow,
                          },
                        ]}
                        size={120}
                        strokeWidth={6}
                        centerValue={statistics?.notes?.total || 0}
                        centerLabel="Total"
                        showLegend={true}
                        legendPosition="right"
                        showValues={true}
                        animate={true}
                      />
                    </div>
                  </div>

                  {/* Active Organizations (Last 30 Days) */}
                  <div className="overview-tile">
                    <div className="tile-title">
                      <div className="tile-icon">
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: "27px", color: COLORS.teal }}
                        >
                          trending_up
                        </span>
                      </div>
                      <div className="tile-title-text">Active Orgs (30d)</div>
                    </div>
                    <div className="content">
                      <CircleChart
                        segments={[
                          {
                            label: "Active",
                            value: statistics?.activeOrganizations || 0,
                            percentage: 100,
                            color: COLORS.teal,
                          },
                        ]}
                        size={120}
                        strokeWidth={6}
                        centerValue={statistics?.activeOrganizations || 0}
                        centerLabel="Active"
                        showLegend={true}
                        legendPosition="right"
                        showValues={true}
                        animate={true}
                      />
                    </div>
                  </div>

                  {/* Active Projects (Last 7 Days) */}
                  <div className="overview-tile">
                    <div className="tile-title">
                      <div className="tile-icon">
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: "27px", color: COLORS.pink }}
                        >
                          event_available
                        </span>
                      </div>
                      <div className="tile-title-text">
                        Active Projects (7d)
                      </div>
                    </div>
                    <div className="content">
                      <CircleChart
                        segments={[
                          {
                            label: "Active",
                            value: statistics?.activeProjects || 0,
                            percentage: 100,
                            color: COLORS.pink,
                          },
                        ]}
                        size={120}
                        strokeWidth={6}
                        centerValue={statistics?.activeProjects || 0}
                        centerLabel="Active"
                        showLegend={true}
                        legendPosition="right"
                        showValues={true}
                        animate={true}
                      />
                    </div>
                  </div>

                  {/* Recent Activity (Last 24h) */}
                  <div className="overview-tile">
                    <div className="tile-title">
                      <div className="tile-icon">
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: "27px", color: COLORS.rose }}
                        >
                          schedule
                        </span>
                      </div>
                      <div className="tile-title-text">
                        Recent Activity (24h)
                      </div>
                    </div>
                    <div className="content">
                      <CircleChart
                        segments={[
                          {
                            label: "Tasks",
                            value: statistics?.recentActivity?.tasks || 0,
                            percentage:
                              ((statistics?.recentActivity?.tasks || 0) /
                                ((statistics?.recentActivity?.tasks || 0) +
                                  (statistics?.recentActivity?.notes || 0) ||
                                  1)) *
                              100,
                            color: COLORS.orange,
                          },
                          {
                            label: "Notes",
                            value: statistics?.recentActivity?.notes || 0,
                            percentage:
                              ((statistics?.recentActivity?.notes || 0) /
                                ((statistics?.recentActivity?.tasks || 0) +
                                  (statistics?.recentActivity?.notes || 0) ||
                                  1)) *
                              100,
                            color: COLORS.yellow,
                          },
                        ]}
                        size={120}
                        strokeWidth={6}
                        centerValue={
                          (statistics?.recentActivity?.tasks || 0) +
                          (statistics?.recentActivity?.notes || 0)
                        }
                        centerLabel="Total"
                        showLegend={true}
                        legendPosition="right"
                        showValues={true}
                        animate={true}
                      />
                    </div>
                  </div>

                  {/* Total Billings */}
                  <div className="overview-tile">
                    <div className="tile-title">
                      <div className="tile-icon">
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: "27px", color: COLORS.green }}
                        >
                          payments
                        </span>
                      </div>
                      <div className="tile-title-text">Total Billings</div>
                    </div>
                    <div
                      className="content"
                      style={{ flexDirection: "column", gap: "0.5rem" }}
                    >
                      <div
                        className="number"
                        style={{ color: COLORS.green, fontSize: "48px" }}
                      >
                        ${statistics?.billings?.total || 0}
                        <span
                          style={{
                            fontSize: "24px",
                            color: COLORS.green,
                            fontWeight: "normal",
                            marginLeft: "8px",
                          }}
                        >
                          /mo
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "var(--secondary-text-color)",
                          textAlign: "center",
                        }}
                      >
                        $8 × {statistics?.billings?.totalMembers || 0} members
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "users" && (
            <div className="users-tab">
              <div className="users-content">
                <div className="users-left-column">
                  <div className="users-column-header">Users</div>
                  <div className="users-search">
                    <input
                      type="text"
                      placeholder="Search users..."
                      className="search-input"
                      value={userSearchText}
                      onChange={(e) => setUserSearchText(e.target.value)}
                    />
                    <span className="material-symbols-outlined search-icon">
                      search
                    </span>
                  </div>
                  <div className="users-list">
                    {loadingUsers ? (
                      <div style={{ padding: "20px", textAlign: "center" }}>
                        <Spinner />
                      </div>
                    ) : (
                      users
                        .filter(
                          (u) =>
                            u.isActive &&
                            (`${u.firstName || ""} ${u.lastName || ""}`
                              .toLowerCase()
                              .includes(userSearchText.toLowerCase()) ||
                              u.email
                                ?.toLowerCase()
                                .includes(userSearchText.toLowerCase())),
                        )
                        .map((user) => (
                          <div
                            key={user._id}
                            className={`user-item ${selectedUser?._id === user._id ? "selected" : ""}`}
                            onClick={() => setSelectedUser(user)}
                          >
                            <div className="user-item-info">
                              <div className="user-item-name">
                                {user.firstName && user.lastName
                                  ? `${user.firstName} ${user.lastName}`
                                  : user.firstName ||
                                    user.lastName ||
                                    "Unnamed"}
                              </div>
                              <div className="user-item-email">
                                {user.email}
                              </div>
                            </div>
                            <button
                              className="delete-user-button"
                              onClick={(e) => handleDeleteUser(user, e)}
                              title="Delete user"
                            >
                              <span className="material-symbols-outlined">
                                delete
                              </span>
                            </button>
                          </div>
                        ))
                    )}
                  </div>
                </div>
                <div className="users-middle-column">
                  <div className="users-column-header">User</div>
                  <div className="users-detail-content">
                    {selectedUser ? (
                      <div className="user-details-wrapper">
                        {/* User Info Section */}
                        <div className="user-info-section">
                          <h3 className="user-name">
                            {selectedUser.firstName && selectedUser.lastName
                              ? `${selectedUser.firstName} ${selectedUser.lastName}`
                              : selectedUser.firstName ||
                                selectedUser.lastName ||
                                "Unnamed User"}
                          </h3>
                          <div className="user-info-item">
                            <span className="info-label">Email:</span>
                            <span className="info-value">
                              {selectedUser.email}
                            </span>
                          </div>
                          <div className="user-info-item">
                            <span className="info-label">Status:</span>
                            <span
                              className={`status-badge ${selectedUser.isActive ? "active" : "inactive"}`}
                            >
                              {selectedUser.isActive ? "Active" : "Pending"}
                            </span>
                          </div>
                        </div>

                        {/* Statistics Section */}
                        {loadingUserStats ? (
                          <div style={{ padding: "20px", textAlign: "center" }}>
                            <Spinner />
                          </div>
                        ) : selectedUserStats ? (
                          <div className="user-stats-section">
                            <h4 className="stats-title">User Statistics</h4>
                            <div className="stats-grid">
                              {/* Organizations */}
                              <div
                                className="stat-tile clickable"
                                onClick={() => fetchDetailData("organizations")}
                              >
                                <div className="stat-header">
                                  <span
                                    className="material-symbols-outlined"
                                    style={{ color: COLORS.indigo }}
                                  >
                                    domain
                                  </span>
                                  <span className="stat-title">
                                    Organizations
                                  </span>
                                </div>
                                <CircleChart
                                  segments={[
                                    {
                                      label: "Owner",
                                      value:
                                        selectedUserStats.statistics
                                          .organizations.owner || 0,
                                      percentage:
                                        ((selectedUserStats.statistics
                                          .organizations.owner || 0) /
                                          (selectedUserStats.statistics
                                            .organizations.total || 1)) *
                                        100,
                                      color: COLORS.indigo,
                                    },
                                    {
                                      label: "Admin",
                                      value:
                                        selectedUserStats.statistics
                                          .organizations.admin || 0,
                                      percentage:
                                        ((selectedUserStats.statistics
                                          .organizations.admin || 0) /
                                          (selectedUserStats.statistics
                                            .organizations.total || 1)) *
                                        100,
                                      color: COLORS.green,
                                    },
                                    {
                                      label: "Member",
                                      value:
                                        selectedUserStats.statistics
                                          .organizations.member || 0,
                                      percentage:
                                        ((selectedUserStats.statistics
                                          .organizations.member || 0) /
                                          (selectedUserStats.statistics
                                            .organizations.total || 1)) *
                                        100,
                                      color: COLORS.blue,
                                    },
                                  ]}
                                  size={80}
                                  strokeWidth={5}
                                  centerValue={
                                    selectedUserStats.statistics.organizations
                                      .total || 0
                                  }
                                  centerLabel="Total"
                                  showLegend={true}
                                  legendPosition="bottom"
                                  showValues={false}
                                  animate={true}
                                />
                              </div>

                              {/* Projects */}
                              <div
                                className="stat-tile clickable"
                                onClick={() => fetchDetailData("projects")}
                              >
                                <div className="stat-header">
                                  <span
                                    className="material-symbols-outlined"
                                    style={{ color: COLORS.blue }}
                                  >
                                    folder_copy
                                  </span>
                                  <span className="stat-title">Projects</span>
                                </div>
                                <CircleChart
                                  segments={[
                                    {
                                      label: "Own",
                                      value:
                                        selectedUserStats.statistics.projects
                                          .own || 0,
                                      percentage:
                                        ((selectedUserStats.statistics.projects
                                          .own || 0) /
                                          (selectedUserStats.statistics.projects
                                            .total || 1)) *
                                        100,
                                      color: COLORS.indigo,
                                    },
                                    {
                                      label: "Admin",
                                      value:
                                        selectedUserStats.statistics.projects
                                          .admin || 0,
                                      percentage:
                                        ((selectedUserStats.statistics.projects
                                          .admin || 0) /
                                          (selectedUserStats.statistics.projects
                                            .total || 1)) *
                                        100,
                                      color: COLORS.green,
                                    },
                                    {
                                      label: "Member",
                                      value:
                                        selectedUserStats.statistics.projects
                                          .member || 0,
                                      percentage:
                                        ((selectedUserStats.statistics.projects
                                          .member || 0) /
                                          (selectedUserStats.statistics.projects
                                            .total || 1)) *
                                        100,
                                      color: COLORS.blue,
                                    },
                                  ]}
                                  size={80}
                                  strokeWidth={5}
                                  centerValue={
                                    selectedUserStats.statistics.projects
                                      .total || 0
                                  }
                                  centerLabel="Total"
                                  showLegend={true}
                                  legendPosition="bottom"
                                  showValues={false}
                                  animate={true}
                                />
                              </div>

                              {/* Teams */}
                              <div
                                className="stat-tile clickable"
                                onClick={() => fetchDetailData("teams")}
                              >
                                <div className="stat-header">
                                  <span
                                    className="material-symbols-outlined"
                                    style={{ color: COLORS.purple }}
                                  >
                                    group
                                  </span>
                                  <span className="stat-title">Teams</span>
                                </div>
                                <CircleChart
                                  segments={[
                                    {
                                      label: "Own",
                                      value:
                                        selectedUserStats.statistics.teams
                                          ?.own || 0,
                                      percentage:
                                        ((selectedUserStats.statistics.teams
                                          ?.own || 0) /
                                          (selectedUserStats.statistics.teams
                                            ?.total || 1)) *
                                        100,
                                      color: COLORS.indigo,
                                    },
                                    {
                                      label: "Member",
                                      value:
                                        selectedUserStats.statistics.teams
                                          ?.member || 0,
                                      percentage:
                                        ((selectedUserStats.statistics.teams
                                          ?.member || 0) /
                                          (selectedUserStats.statistics.teams
                                            ?.total || 1)) *
                                        100,
                                      color: COLORS.blue,
                                    },
                                  ]}
                                  size={80}
                                  strokeWidth={5}
                                  centerValue={
                                    selectedUserStats.statistics.teams?.total ||
                                    0
                                  }
                                  centerLabel="Total"
                                  showLegend={true}
                                  legendPosition="bottom"
                                  showValues={false}
                                  animate={true}
                                />
                              </div>

                              {/* Tasks */}
                              <div
                                className="stat-tile clickable"
                                onClick={() => fetchDetailData("tasks")}
                              >
                                <div className="stat-header">
                                  <span
                                    className="material-symbols-outlined"
                                    style={{ color: COLORS.orange }}
                                  >
                                    checklist
                                  </span>
                                  <span className="stat-title">Tasks</span>
                                </div>
                                <div
                                  className="stat-number"
                                  style={{ color: COLORS.orange }}
                                >
                                  {selectedUserStats.statistics.tasks || 0}
                                </div>
                              </div>

                              {/* Notes */}
                              <div
                                className="stat-tile clickable"
                                onClick={() => fetchDetailData("notes")}
                              >
                                <div className="stat-header">
                                  <span
                                    className="material-symbols-outlined"
                                    style={{ color: COLORS.yellow }}
                                  >
                                    note_stack
                                  </span>
                                  <span className="stat-title">Notes</span>
                                </div>
                                <div
                                  className="stat-number"
                                  style={{ color: COLORS.yellow }}
                                >
                                  {selectedUserStats.statistics.notes || 0}
                                </div>
                              </div>

                              {/* Billing */}
                              <div
                                className="stat-tile clickable"
                                onClick={() => fetchDetailData("billing")}
                              >
                                <div className="stat-header">
                                  <span
                                    className="material-symbols-outlined"
                                    style={{ color: COLORS.green }}
                                  >
                                    payments
                                  </span>
                                  <span className="stat-title">Billing</span>
                                </div>
                                <div
                                  className="stat-billing"
                                  style={{ color: COLORS.green }}
                                >
                                  <div className="billing-amount">
                                    $
                                    {selectedUserStats.statistics.billing
                                      ?.total || 0}
                                    <span className="billing-period">/mo</span>
                                  </div>
                                  <div className="billing-details">
                                    {selectedUserStats.statistics.billing
                                      ?.members || 0}{" "}
                                    members
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <div
                        style={{
                          color: "var(--secondary-text-color)",
                          textAlign: "center",
                          paddingTop: "40px",
                        }}
                      >
                        Select a user to view details
                      </div>
                    )}
                  </div>
                </div>
                <div className="users-right-column">
                  <div className="users-column-header">
                    {selectedDetailType
                      ? selectedDetailType.charAt(0).toUpperCase() +
                        selectedDetailType.slice(1)
                      : "Details"}
                  </div>
                  <div className="users-detail-content">
                    {selectedUser ? (
                      loadingDetails ? (
                        <div
                          style={{
                            padding: "20px",
                            textAlign: "center",
                          }}
                        >
                          <Spinner />
                        </div>
                      ) : detailData && detailData.length > 0 ? (
                        <div className="detail-list">
                          {selectedDetailType === "organizations" &&
                            detailData.map((org) => (
                              <div key={org._id} className="detail-item">
                                <div className="detail-item-header">
                                  <span className="detail-item-name">
                                    {org.name}
                                  </span>
                                  <span
                                    className={`detail-item-badge ${org.role}`}
                                  >
                                    {org.role}
                                  </span>
                                </div>
                                <div className="detail-item-info">
                                  <span>{org.memberCount} members</span>
                                  <span>
                                    {new Date(
                                      org.createdAt,
                                    ).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            ))}

                          {selectedDetailType === "projects" &&
                            detailData.map((proj) => (
                              <div key={proj._id} className="detail-item">
                                <div className="detail-item-header">
                                  <span className="detail-item-name">
                                    {proj.name}
                                  </span>
                                  <span
                                    className={`detail-item-badge ${proj.role}`}
                                  >
                                    {proj.role}
                                  </span>
                                </div>
                                <div className="detail-item-info">
                                  <span>{proj.organizationName}</span>
                                  <span>{proj.memberCount} members</span>
                                </div>
                              </div>
                            ))}

                          {selectedDetailType === "teams" &&
                            detailData.map((team) => (
                              <div key={team._id} className="detail-item">
                                <div className="detail-item-header">
                                  <span className="detail-item-name">
                                    {team.name}
                                  </span>
                                  <span
                                    className={`detail-item-badge ${team.role}`}
                                  >
                                    {team.role}
                                  </span>
                                </div>
                                <div className="detail-item-info">
                                  <span>{team.organizationName}</span>
                                  <span>{team.memberCount} members</span>
                                </div>
                              </div>
                            ))}

                          {selectedDetailType === "tasks" &&
                            detailData.map((task) => (
                              <div key={task._id} className="detail-item">
                                <div className="detail-item-header">
                                  <span className="detail-item-name">
                                    {task.title}
                                  </span>
                                </div>
                                <div className="detail-item-info">
                                  <span>{task.projectName}</span>
                                  <span>{task.responsible}</span>
                                </div>
                              </div>
                            ))}

                          {selectedDetailType === "notes" &&
                            detailData.map((note) => (
                              <div key={note._id} className="detail-item">
                                <div className="detail-item-header">
                                  <span className="detail-item-name">
                                    {note.title}
                                  </span>
                                </div>
                                <div className="detail-item-info">
                                  <span>{note.projectName}</span>
                                  {note.content && (
                                    <span className="note-preview">
                                      {note.content}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}

                          {selectedDetailType === "billing" &&
                            detailData.map((org) => (
                              <div key={org._id} className="detail-item">
                                <div className="detail-item-header">
                                  <span className="detail-item-name">
                                    {org.organizationName}
                                  </span>
                                  <span className="detail-item-badge billing">
                                    ${org.cost}/mo
                                  </span>
                                </div>
                                <div className="detail-item-info">
                                  <span>{org.memberCount} members</span>
                                </div>
                                {org.members && org.members.length > 0 && (
                                  <div className="detail-item-members">
                                    {org.members.map((member) => (
                                      <div
                                        key={member._id}
                                        className="member-item"
                                      >
                                        <span className="member-name">
                                          {member.name}
                                        </span>
                                        <span className="member-email">
                                          {member.email}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      ) : detailData ? (
                        <div
                          style={{
                            color: "var(--secondary-text-color)",
                            textAlign: "center",
                            paddingTop: "40px",
                          }}
                        >
                          No {selectedDetailType} found
                        </div>
                      ) : (
                        <div
                          style={{
                            color: "var(--secondary-text-color)",
                            textAlign: "center",
                            paddingTop: "40px",
                          }}
                        >
                          Click on a tile to view details
                        </div>
                      )
                    ) : (
                      <div
                        style={{
                          color: "var(--secondary-text-color)",
                          textAlign: "center",
                          paddingTop: "40px",
                        }}
                      >
                        Select a user to view details
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "enterprise" && (
            <div className="super-dashboard-content enterprise-tab">
              <div className="enterprise-header">
                <h1>Enterprise Organizations</h1>
                <button
                  className="generate-all-btn"
                  onClick={async () => {
                    if (
                      !confirm(
                        "Generate invoices for all enterprise organizations? This will create invoices for orgs that haven't been invoiced this month.",
                      )
                    ) {
                      return;
                    }
                    setGeneratingAll(true);
                    try {
                      const result = await generateAllMonthlyInvoices(token);
                      alert(
                        `Invoice generation complete!\n\nSuccessful: ${result.success.length}\nFailed: ${result.failed.length}\nSkipped: ${result.skipped.length}`,
                      );
                      fetchEnterpriseOrgs(); // Refresh list
                    } catch (error) {
                      alert("Failed to generate invoices: " + error.message);
                    } finally {
                      setGeneratingAll(false);
                    }
                  }}
                  disabled={generatingAll || loadingEnterprise}
                >
                  <span className="material-symbols-outlined">
                    receipt_long
                  </span>
                  {generatingAll
                    ? "Generating Invoices..."
                    : "Generate Monthly Invoices"}
                </button>
              </div>

              {loadingEnterprise ? (
                <div className="loading-container">
                  <Spinner />
                </div>
              ) : enterpriseOrgs.length === 0 ? (
                <div className="empty-state">
                  <span className="material-symbols-outlined">business</span>
                  <p>No enterprise organizations yet</p>
                  <small>
                    Enterprise organizations use invoice-based billing
                  </small>
                </div>
              ) : (
                <>
                  <div className="enterprise-summary">
                    <div className="summary-card">
                      <div className="summary-icon">
                        <span className="material-symbols-outlined">
                          business
                        </span>
                      </div>
                      <div className="summary-info">
                        <div className="summary-value">
                          {enterpriseOrgs.length}
                        </div>
                        <div className="summary-label">
                          Enterprise Organizations
                        </div>
                      </div>
                    </div>

                    <div className="summary-card">
                      <div className="summary-icon">
                        <span className="material-symbols-outlined">group</span>
                      </div>
                      <div className="summary-info">
                        <div className="summary-value">
                          {enterpriseOrgs.reduce(
                            (sum, org) => sum + org.currentSeats,
                            0,
                          )}
                        </div>
                        <div className="summary-label">Total Active Seats</div>
                      </div>
                    </div>

                    <div className="summary-card">
                      <div className="summary-icon">
                        <span className="material-symbols-outlined">
                          payments
                        </span>
                      </div>
                      <div className="summary-info">
                        <div className="summary-value">
                          $
                          {(
                            enterpriseOrgs.reduce(
                              (sum, org) => sum + org.currentSeats,
                              0,
                            ) * 8
                          ).toFixed(2)}
                        </div>
                        <div className="summary-label">
                          Monthly Revenue (MRR)
                        </div>
                      </div>
                    </div>

                    <div className="summary-card">
                      <div className="summary-icon warning">
                        <span className="material-symbols-outlined">
                          warning
                        </span>
                      </div>
                      <div className="summary-info">
                        <div className="summary-value">
                          {
                            enterpriseOrgs.filter(
                              (org) => org.invoiceStatus === "overdue",
                            ).length
                          }
                        </div>
                        <div className="summary-label">Overdue Invoices</div>
                      </div>
                    </div>
                  </div>

                  <div className="enterprise-orgs-list">
                    {enterpriseOrgs.map((org) => (
                      <div key={org.id} className="enterprise-org-card">
                        <div className="org-header">
                          <div className="org-title">
                            <h3>{org.name}</h3>
                            <span
                              className={`status-badge ${org.invoiceStatus}`}
                            >
                              {org.invoiceStatus === "current" && "✓ Current"}
                              {org.invoiceStatus === "overdue" && "⚠ Overdue"}
                              {org.invoiceStatus === "grace_period" &&
                                "⏰ Grace Period"}
                            </span>
                          </div>
                          <button
                            className="generate-invoice-btn"
                            onClick={async () => {
                              if (
                                !confirm(
                                  `Generate invoice for ${org.name}?\n\nThis will bill for ${org.currentSeats} active member${org.currentSeats > 1 ? "s" : ""} at $8/month = $${(org.currentSeats * 8).toFixed(2)}`,
                                )
                              ) {
                                return;
                              }
                              setGeneratingInvoice(org.id);
                              try {
                                const result = await generateInvoice(
                                  org.id,
                                  token,
                                );
                                alert(
                                  `Invoice generated successfully!\n\nAmount: $${(result.amount / 100).toFixed(2)}\nSeats: ${result.seats}\n\nInvoice has been sent to ${org.billingContact.email}`,
                                );
                                fetchEnterpriseOrgs(); // Refresh
                              } catch (error) {
                                alert(
                                  "Failed to generate invoice: " +
                                    error.message,
                                );
                              } finally {
                                setGeneratingInvoice(null);
                              }
                            }}
                            disabled={generatingInvoice === org.id}
                          >
                            <span className="material-symbols-outlined">
                              receipt
                            </span>
                            {generatingInvoice === org.id
                              ? "Generating..."
                              : "Generate Invoice"}
                          </button>
                        </div>

                        <div className="org-details">
                          <div className="detail-row">
                            <div className="detail-item">
                              <span className="material-symbols-outlined">
                                person
                              </span>
                              <div>
                                <div className="detail-label">Owner</div>
                                <div className="detail-value">
                                  {org.owner.name}
                                </div>
                                <div className="detail-sub">
                                  {org.owner.email}
                                </div>
                              </div>
                            </div>

                            <div className="detail-item">
                              <span className="material-symbols-outlined">
                                email
                              </span>
                              <div>
                                <div className="detail-label">
                                  Billing Contact
                                </div>
                                <div className="detail-value">
                                  {org.billingContact.name}
                                </div>
                                <div className="detail-sub">
                                  {org.billingContact.email}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="detail-row">
                            <div className="detail-item">
                              <span className="material-symbols-outlined">
                                group
                              </span>
                              <div>
                                <div className="detail-label">Members</div>
                                <div className="detail-value">
                                  {org.currentSeats} active
                                  {org.pendingSeats > 0 &&
                                    `, ${org.pendingSeats} pending`}
                                </div>
                                {org.contractedSeats && (
                                  <div className="detail-sub">
                                    Limit: {org.contractedSeats} seats
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="detail-item">
                              <span className="material-symbols-outlined">
                                calendar_month
                              </span>
                              <div>
                                <div className="detail-label">
                                  Billing Cycle
                                </div>
                                <div className="detail-value">
                                  {org.billingCycle.charAt(0).toUpperCase() +
                                    org.billingCycle.slice(1)}
                                </div>
                                <div className="detail-sub">
                                  {org.lastInvoiceDate
                                    ? `Last: ${new Date(org.lastInvoiceDate).toLocaleDateString()}`
                                    : "No invoices yet"}
                                </div>
                              </div>
                            </div>

                            <div className="detail-item">
                              <span className="material-symbols-outlined">
                                payments
                              </span>
                              <div>
                                <div className="detail-label">
                                  Monthly Billing
                                </div>
                                <div className="detail-value">
                                  ${(org.currentSeats * 8).toFixed(2)}
                                </div>
                                <div className="detail-sub">
                                  ${org.currentSeats} × $8/seat
                                </div>
                              </div>
                            </div>

                            <div className="detail-item">
                              <span className="material-symbols-outlined">
                                schedule
                              </span>
                              <div>
                                <div className="detail-label">Created</div>
                                <div className="detail-value">
                                  {new Date(org.createdAt).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "system-notification" && <SystemBannersTab />}

          {activeTab === "stash" && (
            <div className="super-dashboard-content">
              <h1>Stash</h1>

              <div className="wipe-user-section">
                <h2>Clean User Data (Testing Only)</h2>
                <p className="warning-text">
                  ⚠️ This will remove all user data but keep the account active.
                  Cannot be undone!
                </p>

                <div className="wipe-form">
                  <input
                    type="email"
                    placeholder="Enter email address..."
                    value={cleanEmail}
                    onChange={(e) => setCleanEmail(e.target.value)}
                    disabled={isCleaning}
                    className="wipe-input"
                    onKeyPress={(e) =>
                      e.key === "Enter" && handleCleanUserData()
                    }
                  />
                  <button
                    onClick={handleCleanUserData}
                    disabled={isCleaning || !cleanEmail.trim()}
                    className="wipe-button clean-button"
                  >
                    {isCleaning ? "Cleaning..." : "Clean User Data"}
                  </button>
                </div>

                {cleanResult && (
                  <div
                    className={`wipe-result ${cleanResult.success ? "success" : "error"}`}
                  >
                    <p>
                      <strong>{cleanResult.message}</strong>
                    </p>
                    {cleanResult.details && (
                      <ul>
                        {cleanResult.details.organizationsRemoved > 0 && (
                          <li>
                            ✓ Removed from{" "}
                            {cleanResult.details.organizationsRemoved}{" "}
                            organization(s)
                          </li>
                        )}
                        {cleanResult.details.organizationsDeleted > 0 && (
                          <li>
                            ✓ Deleted {cleanResult.details.organizationsDeleted}{" "}
                            owned organization(s)
                          </li>
                        )}
                        {cleanResult.details.projectsRemoved > 0 && (
                          <li>
                            ✓ Removed from {cleanResult.details.projectsRemoved}{" "}
                            project(s)
                          </li>
                        )}
                        {cleanResult.details.tasksCleared > 0 && (
                          <li>
                            ✓ Cleared from {cleanResult.details.tasksCleared}{" "}
                            task(s)
                          </li>
                        )}
                        {cleanResult.details.notesDeleted > 0 && (
                          <li>
                            ✓ Deleted {cleanResult.details.notesDeleted} note(s)
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <div className="wipe-user-section">
                <h2>Wipe User (Testing Only)</h2>
                <p className="warning-text">
                  ⚠️ This will completely remove a user and all their data.
                  Cannot be undone!
                </p>

                <div className="wipe-form">
                  <input
                    type="email"
                    placeholder="Enter email address..."
                    value={wipeEmail}
                    onChange={(e) => setWipeEmail(e.target.value)}
                    disabled={isWiping}
                    className="wipe-input"
                    onKeyPress={(e) => e.key === "Enter" && handleWipeUser()}
                  />
                  <button
                    onClick={handleWipeUser}
                    disabled={isWiping || !wipeEmail.trim()}
                    className="wipe-button"
                  >
                    {isWiping ? "Wiping..." : "Wipe User"}
                  </button>
                </div>

                {wipeResult && (
                  <div
                    className={`wipe-result ${wipeResult.success ? "success" : "error"}`}
                  >
                    <p>
                      <strong>{wipeResult.message}</strong>
                    </p>
                    {wipeResult.details && (
                      <ul>
                        {wipeResult.details.userDeleted && (
                          <li>✓ User account deleted</li>
                        )}
                        {wipeResult.details.organizationsRemoved > 0 && (
                          <li>
                            ✓ Removed from{" "}
                            {wipeResult.details.organizationsRemoved}{" "}
                            organization(s)
                          </li>
                        )}
                        {wipeResult.details.organizationsDeleted > 0 && (
                          <li>
                            ✓ Deleted {wipeResult.details.organizationsDeleted}{" "}
                            owned organization(s)
                          </li>
                        )}
                        {wipeResult.details.projectsRemoved > 0 && (
                          <li>
                            ✓ Removed from {wipeResult.details.projectsRemoved}{" "}
                            project(s)
                          </li>
                        )}
                        {wipeResult.details.tasksCleared > 0 && (
                          <li>
                            ✓ Cleared from {wipeResult.details.tasksCleared}{" "}
                            task(s)
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <div className="future-features">
                <h2>Coming Soon</h2>
                <ol>
                  <li>System banner</li>
                  <li>Overview of all organizations</li>
                  <li>Manage org admins and their permissions</li>
                  <li>Access detailed analytics across organizations</li>
                  <li>System-wide settings and configurations</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete User Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="modal-overlay" onClick={handleCancelDelete}>
          <div
            className="delete-user-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Delete User</h3>
              <button
                className="close-modal-button"
                onClick={handleCancelDelete}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="modal-content">
              <p className="warning-message">
                Are you sure you want to permanently delete this user?
              </p>
              <div className="user-info">
                <div className="user-name">
                  {userToDelete.firstName && userToDelete.lastName
                    ? `${userToDelete.firstName} ${userToDelete.lastName}`
                    : userToDelete.firstName ||
                      userToDelete.lastName ||
                      "Unnamed"}
                </div>
                <div className="user-email">{userToDelete.email}</div>
              </div>
              <div className="warning-details">
                <p>This action will:</p>
                <ul>
                  <li>Delete the user account permanently</li>
                  <li>Remove them from all organizations and projects</li>
                  <li>Delete their owned organizations</li>
                  <li>Cancel any active Stripe subscriptions</li>
                  <li>Cannot be undone</li>
                </ul>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="delete-confirm-button"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperDashboard;
